/* Progressive enhancement of native Liquid sections; the original description stays server-rendered. */
(() => {
  'use strict';
  const bounded = (value, fallback, max) => Math.max(1, Math.min(max, Number.parseInt(value, 10) || fallback));
  const text = (node) => {
    const copy = node.cloneNode(true);
    copy.querySelectorAll('script,style,template,noscript,[hidden]').forEach((child) => child.remove());
    return copy.textContent.replace(/\s+/g, ' ').trim();
  };
  const node = (tag, content, className) => {
    const result = document.createElement(tag);
    if (content) result.textContent = content;
    if (className) result.className = className;
    return result;
  };
  const matches = (heading, words) => words.split(',').map((word) => word.trim().toLocaleLowerCase()).filter(Boolean).some((word) => heading.includes(word));
  const titleCase = (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  const period = (count, unit) => count + ' ' + (/^y/i.test(unit) ? 'Year' : /^mo/i.test(unit) ? 'Month' : 'Day') + (Number(count) === 1 ? '' : 's');

  // Only what the description actually states: a warranty period, a return/replacement window, or non-returnable.
  function descriptionTerms(content) {
    const found = [];
    const warrantyAfter = content.match(/\b(\d{1,2})\s*-?\s*(years?|yrs?|months?)\b[^.?!\n]{0,24}?\b(warranty|guarantee)\b/i);
    const warrantyBefore = !warrantyAfter && content.match(/\b(warranty|guarantee)\b[^.?!\n]{0,20}?\b(\d{1,2})\s*-?\s*(years?|yrs?|months?)\b/i);
    if (warrantyAfter) found.push(period(warrantyAfter[1], warrantyAfter[2]) + ' ' + titleCase(warrantyAfter[3]));
    else if (warrantyBefore) found.push(period(warrantyBefore[2], warrantyBefore[3]) + ' ' + titleCase(warrantyBefore[1]));
    else if (/\blifetime\s+warranty\b/i.test(content)) found.push('Lifetime Warranty');
    else {
      const stated = content.match(/\b(brand|manufacturer'?s?)\s+warranty\b/i);
      if (stated) found.push(/^brand$/i.test(stated[1]) ? 'Brand Warranty' : 'Manufacturer Warranty');
    }

    const returnAfter = content.match(/\b(\d{1,2})\s*-?\s*days?\b[^.?!\n]{0,20}?\b(return|replacement|refund|exchange)s?\b/i);
    const returnBefore = !returnAfter && content.match(/\b(return|replacement|refund|exchange)s?\b[^.?!\n]{0,24}?\bwithin\s*(\d{1,2})\s*days?\b/i);
    if (returnAfter) found.push(period(returnAfter[1], 'day') + ' ' + titleCase(returnAfter[2]));
    else if (returnBefore) found.push(period(returnBefore[2], 'day') + ' ' + titleCase(returnBefore[1]));
    if (/\bnon[-\s]?returnable\b|\bnot\s+returnable\b/i.test(content)) found.push('Non-returnable');
    return found;
  }

  function warrantyEntry(source, options) {
    const highlights = descriptionTerms(text(source));
    const terms = (options.warrantyTerms || '').split(/\n+/).map((line) => line.trim()).filter(Boolean);
    if (!highlights.length && !terms.length) return null;
    return { type: 'warranty', highlights, terms, policyUrl: options.policyUrl, helpUrl: options.helpUrl };
  }

  function extract(source, options = {}) {
    const bulletLimit = bounded(options.bullets, 3, 12);
    const rowLimit = bounded(options.rows, 4, 12);
    const groups = { features: null, specifications: null, included: null, warranty: null };
    const keywords = {
      features: options.featureKeywords || 'feature,benefit,highlight,at a glance',
      specifications: options.specKeywords || 'specification,technical,product details',
      included: options.includedKeywords || "included,in the box,package contents,box contents",
      warranty: options.warrantyKeywords || 'warranty,guarantee,returns'
    };
    let heading = '';
    let kind = '';
    let fallbackList = null;
    let fallbackTable = null;
    let fallbackParagraph = '';
    for (const element of source.querySelectorAll('h1,h2,h3,h4,h5,h6,ul,ol,table,p')) {
      if (element.closest('[hidden],script,style,template')) continue;
      if (element.parentElement.closest('ul,ol,table')) continue;
      if (/^H[1-6]$/.test(element.tagName)) {
        heading = text(element).toLocaleLowerCase();
        kind = Object.keys(keywords).find((key) => matches(heading, keywords[key])) || '';
        continue;
      }
      // Warranty content is built from the store terms plus stated periods, never from whatever
      // follows a heading that merely contains "warranty" (product titles often do).
      if (kind === 'warranty') continue;
      const ignoredSection = /shipping|delivery|warning|caution|disclaimer|contraindication/.test(heading);
      if (element.matches('ul,ol')) {
        const listItems = Array.from(element.children).filter((item) => item.tagName === 'LI' && text(item)).slice(0, bulletLimit);
        const items = listItems.map(text);
        const labels = listItems.map((item, index) => {
          const emphasis = item.querySelector('strong,b');
          const emphasized = emphasis ? text(emphasis) : '';
          const label = emphasized.replace(/:\s*$/, '');
          const isLabel = /:\s*$/.test(emphasized) || items[index].startsWith(emphasized + ':') || items[index] === emphasized;
          return isLabel && label && label.length <= 90 && items[index].startsWith(label) ? label : items[index];
        });
        if (!items.length) continue;
        const entry = { type: 'list', items, labels };
        if (kind && !groups[kind]) groups[kind] = entry;
        else if (!kind && !ignoredSection && !fallbackList) fallbackList = entry;
      } else if (element.tagName === 'TABLE') {
        const rows = Array.from(element.rows).filter((row) => row.closest('table') === element).slice(0, rowLimit).map((row) =>
          Array.from(row.cells).slice(0, 8).map((cell) => ({ text: text(cell), header: cell.tagName === 'TH' }))
        ).filter((row) => row.some((cell) => cell.text));
        if (!rows.length) continue;
        const entry = { type: 'table', rows };
        if (kind && !groups[kind]) groups[kind] = entry;
        else if (!kind && !ignoredSection && !fallbackTable) fallbackTable = entry;
      } else if (!kind && !ignoredSection && !fallbackParagraph) {
        fallbackParagraph = text(element);
      }
    }
    groups.features ||= fallbackList;
    groups.specifications ||= fallbackTable;
    delete groups.warranty;
    if (!Object.values(groups).some(Boolean) && fallbackParagraph) {
      groups.details = { type: 'paragraph', text: fallbackParagraph };
    }
    groups.warranty = warrantyEntry(source, options);
    return groups;
  }

  function renderEntry(entry) {
    if (entry.type === 'warranty') {
      const box = node('div', '', 'mhc-warranty');
      if (entry.highlights.length) {
        box.append(node('p', 'For this product', 'mhc-warranty__label'));
        const chips = node('ul', '', 'mhc-warranty__chips');
        entry.highlights.forEach((item) => chips.append(node('li', item)));
        box.append(chips);
      }
      if (entry.terms.length) {
        box.append(node('p', 'Return & replacement terms', 'mhc-warranty__label'));
        const list = node('ul', '', 'mhc-warranty__terms');
        entry.terms.forEach((item) => list.append(node('li', item)));
        box.append(list);
      }
      const links = node('p', '', 'mhc-warranty__links');
      [[entry.policyUrl, 'Full return & refund policy'], [entry.helpUrl, 'Raise a return request']].forEach(([href, label]) => {
        if (!href) return;
        const link = node('a', label);
        link.href = href;
        links.append(link);
      });
      if (links.children.length) box.append(links);
      return box;
    }
    if (entry.type === 'list') {
      const list = node('ul');
      entry.items.forEach((item) => list.append(node('li', item)));
      return list;
    }
    if (entry.type === 'table') {
      const wrapper = node('div', '', 'mhc-summary-table-wrap');
      const table = node('table', '', 'mhc-summary-table');
      const body = node('tbody');
      entry.rows.forEach((cells) => {
        const row = node('tr');
        cells.forEach((cell) => row.append(node(cell.header ? 'th' : 'td', cell.text)));
        body.append(row);
      });
      table.append(body);
      wrapper.append(table);
      return wrapper;
    }
    return node('p', entry.text);
  }

  function revealDescription(source) {
    let parent = source.parentElement;
    while (parent) {
      if (parent.tagName === 'DETAILS') parent.open = true;
      if (parent.tagName === 'COLLAPSIBLE-CONTENT') parent.setAttribute('open', '');
      if (parent.classList.contains('product-tabs__tab-item-wrapper')) parent.hidden = false;
      parent = parent.parentElement;
    }
    const destination = source.closest('.mhc-full-description') || source;
    destination.tabIndex = -1;
    destination.focus({ preventScroll: true });
    destination.scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }

  class DescriptionSummary extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.addEventListener('click', (event) => {
        if (!event.target.closest('.mhc-read-more')) return;
        const source = document.getElementById(this.dataset.source);
        if (!source) return;
        event.preventDefault();
        revealDescription(source);
      }, { signal: this.controller.signal });
      queueMicrotask(() => this.refresh());
    }
    disconnectedCallback() { this.controller?.abort(); this.revealObserver?.disconnect(); }
    refresh() {
      const source = document.getElementById(this.dataset.source);
      this.revealObserver?.disconnect();
      this.classList.remove('mhc-reveal-pending', 'mhc-reveal');
      this.replaceChildren();
      this.hidden = true;
      if (!source) return;
      const groups = extract(source, this.dataset);
      if (this.dataset.mode === 'glance') {
        const entry = groups.features;
        if (!entry || entry.type !== 'list') return;
        const glance = this.dataset.compact === 'false' ? entry : { ...entry, items: entry.labels || entry.items };
        this.append(node('h2', this.dataset.title || 'At a glance'), renderEntry(glance));
      } else {
        let index = 0;
        for (const [kind, entry] of Object.entries(groups)) {
          if (!entry) continue;
          const details = node('details', '', 'mhc-summary');
          details.open = this.dataset.open === 'all' || (this.dataset.open !== 'none' && index === 0);
          details.dataset.pinned = String(details.open);
          const defaultLabels = { features: 'Key features', specifications: 'Specifications', included: "What's included", warranty: 'Warranty & returns', details: 'Product details' };
          const summary = node('summary', this.dataset[kind + 'Label'] || defaultLabels[kind]);
          details.append(summary);
          const content = node('div', '', 'mhc-summary-content');
          content.append(renderEntry(entry));
          if (entry.type !== 'warranty' || entry.highlights.length) {
            const link = node('a', this.dataset.readMore || 'Read more in full description', 'mhc-read-more');
            link.href = '#' + source.id;
            content.append(link);
          }
          details.append(content);
          this.append(details);
          index += 1;
        }

        // Smart Hover & Click Accordion Controller
        const detailsList = Array.from(this.querySelectorAll('.mhc-summary'));
        detailsList.forEach((details) => {
          const summary = details.querySelector('summary');
          if (!summary) return;

          details.addEventListener('mouseenter', () => {
            if (details.dataset.pinned !== 'true') {
              details.open = true;
            }
            // Auto close any other unpinned accordion
            detailsList.forEach((other) => {
              if (other !== details && other.dataset.pinned !== 'true') {
                other.open = false;
              }
            });
          });

          summary.addEventListener('click', (e) => {
            e.preventDefault();
            const wasPinned = details.dataset.pinned === 'true';
            if (wasPinned) {
              details.dataset.pinned = 'false';
              details.open = false;
            } else {
              details.dataset.pinned = 'true';
              details.open = true;
            }
          });
        });

        this.addEventListener('mouseleave', () => {
          detailsList.forEach((details) => {
            if (details.dataset.pinned !== 'true') {
              details.open = false;
            }
          });
        });
      }
      this.hidden = !this.children.length;
      if (this.dataset.mode !== 'glance' && this.querySelector('.mhc-summary[open]') && 'IntersectionObserver' in window && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.classList.add('mhc-reveal-pending');
        this.revealObserver = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.revealObserver.disconnect();
          this.classList.replace('mhc-reveal-pending', 'mhc-reveal');
        }, { threshold: 0.2 });
        this.revealObserver.observe(this);
      }
    }
  }
  if (!customElements.get('meddey-description-summary')) customElements.define('meddey-description-summary', DescriptionSummary);
  window.MeddeyHomeCare = { extract };
  const refresh = () => document.querySelectorAll('meddey-description-summary').forEach((element) => element.refresh());
  document.addEventListener('shopify:section:load', refresh);
  document.addEventListener('DOMContentLoaded', refresh, { once: true });

  document.addEventListener('click', (event) => {
    document.querySelectorAll('.mhc-more[open]').forEach((menu) => {
      if (!menu.contains(event.target) || event.target.closest('a')) menu.open = false;
    });
    const button = event.target.closest('[data-mhc-check-delivery]');
    if (!button) return;
    const container = button.closest('.mhc-delivery');
    const input = container.querySelector('input');
    const status = container.querySelector('[role=status]');
    const valid = /^\d{6}$/.test(input.value.trim());
    input.setAttribute('aria-invalid', String(!valid));
    status.textContent = valid ? container.dataset.unconfirmed : container.dataset.invalid;
    if (!valid) input.focus();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('.mhc-more[open]').forEach((menu) => {
      menu.open = false;
      menu.querySelector('summary').focus();
    });
  });
})();
